'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Spinner } from '@common/components';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import useEmblaCarousel from 'embla-carousel-react';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { UnauthInfiniteQueryOptions } from '@/api';
import { ReviewDetailSessionPortal } from '@/app/donation/_components/ReviewDetailSessionPortal';
import { Image } from '@/components/client';
import { SessionPortal } from '@/components/organism/SessionPortal/SessionPortal';
import { SELECTOR } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { openResponsiveDialog } from '@/stores/ResponsiveDialogStore';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';
import { PhotoReviewsModalContent } from '../gifts/[productNumber]/_components/PhotoReviewsModalContent';
const INITIAL_PAGE = 1;
const INITIAL_SIZE = 10;
interface PhotoReviewSwiperProps {
  photoReviewList: {
    fileUrl: string;
    reviewId: number;
  }[];
  photoReviewCount: number;
  productNumber?: string;
  institutionId?: number;
  className?: string;
}
export const PhotoReviewSwiper = t0 => {
  const $ = _c(66);
  const {
    photoReviewList,
    photoReviewCount,
    productNumber,
    institutionId,
    className
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const [isOpen, setIsOpen] = useState(false);
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("detailReviewId");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const detailReviewIdParam = t1;
  const detailReviewId = detailReviewIdParam ? Number(detailReviewIdParam) : undefined;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      align: "start",
      dragFree: true,
      containScroll: "trimSnaps"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [emblaRef] = useEmblaCarousel(t2);
  let t3;
  if ($[3] !== institutionId || $[4] !== photoReviewCount || $[5] !== photoReviewList || $[6] !== productNumber) {
    t3 = reviewId => {
      openResponsiveDialog({
        component: PhotoReviewsModalContent,
        modalHeader: "\uD3EC\uD1A0\uD6C4\uAE30",
        className: {
          modal: "max-w-[900px]"
        },
        props: {
          productNumber,
          institutionId,
          photoReviewList,
          photoReviewCount,
          initialReviewId: reviewId
        },
        drawerNoPadding: true
      });
    };
    $[3] = institutionId;
    $[4] = photoReviewCount;
    $[5] = photoReviewList;
    $[6] = productNumber;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleDetailReviewId = t3;
  let t4;
  if ($[8] !== institutionId || $[9] !== productNumber) {
    t4 = filledInfiniteQueryOptions(UnauthInfiniteQueryOptions["get:/wello/v2/allows/home/gift-review/photo"]({
      query: {
        productNumber,
        institutionId,
        page: INITIAL_PAGE,
        size: INITIAL_SIZE
      }
    }));
    $[8] = institutionId;
    $[9] = productNumber;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = (!!productNumber || !!institutionId) && isOpen;
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = {
      ...t4,
      enabled: t5
    };
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isError
  } = useInfiniteQuery(t6);
  const infinitePhotoReviewList = data?.pages.flatMap(_temp) ?? [];
  const {
    ref,
    inView
  } = useInView();
  let t7;
  if ($[14] !== fetchNextPage || $[15] !== inView || $[16] !== isError || $[17] !== isFetching) {
    t7 = () => {
      if (!isFetching && !isError && inView) {
        fetchNextPage();
      }
    };
    $[14] = fetchNextPage;
    $[15] = inView;
    $[16] = isError;
    $[17] = isFetching;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== fetchNextPage || $[20] !== hasNextPage || $[21] !== inView || $[22] !== isError || $[23] !== isFetching) {
    t8 = [inView, isFetching, isError, fetchNextPage, hasNextPage];
    $[19] = fetchNextPage;
    $[20] = hasNextPage;
    $[21] = inView;
    $[22] = isError;
    $[23] = isFetching;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  useEffect(t7, t8);
  if (photoReviewCount < 1) {
    return null;
  }
  let t9;
  if ($[25] !== className) {
    t9 = cn("flex flex-col pb-24", className);
    $[25] = className;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cn("flex items-center justify-between py-12", "mobile-tablet:py-16");
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== photoReviewCount) {
    t11 = photoReviewCount.toLocaleString();
    $[28] = photoReviewCount;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11) {
    t12 = <h2 className="font-700 text-Gray-900 typo-body2">포토후기 {t11}</h2>;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== institutionId || $[33] !== isDesktopViewport || $[34] !== photoReviewCount || $[35] !== photoReviewList || $[36] !== productNumber) {
    t13 = () => {
      if (isDesktopViewport) {
        openResponsiveDialog({
          component: PhotoReviewsModalContent,
          modalHeader: "\uD3EC\uD1A0\uD6C4\uAE30 \uC804\uCCB4\uBCF4\uAE30",
          className: {
            modal: "max-w-[900px]"
          },
          props: {
            institutionId,
            productNumber,
            photoReviewList,
            photoReviewCount
          }
        });
      } else {
        setIsOpen(true);
      }
    };
    $[32] = institutionId;
    $[33] = isDesktopViewport;
    $[34] = photoReviewCount;
    $[35] = photoReviewList;
    $[36] = productNumber;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SvgChevronright className="size-12" />;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t13) {
    t15 = <Button theme="sub-text" className="flex size-fit items-center gap-4 px-4 py-10 font-400 text-Gray-900 typo-body4" onClick={t13}>더보기{t14}</Button>;
    $[39] = t13;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t12 || $[42] !== t15) {
    t16 = <div className={t10}>{t12}{t15}</div>;
    $[41] = t12;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== handleDetailReviewId || $[45] !== photoReviewList) {
    let t18;
    if ($[47] !== handleDetailReviewId) {
      t18 = t19 => {
        const {
          fileUrl,
          reviewId: reviewId_0
        } = t19;
        return <div key={reviewId_0} className="w-fit flex-[0_0_auto]"><button type="button" className={cn("relative size-104 overflow-hidden rounded-small border border-Gray-black/5 transition-opacity duration-200", "hover:opacity-80", "active:opacity-80", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500")} onClick={() => handleDetailReviewId(reviewId_0)}><Image src={fileUrl} alt={"\uD3EC\uD1A0\uD6C4\uAE30 \uC774\uBBF8\uC9C0"} width={850} height={850} className="size-full object-cover object-center" /></button></div>;
      };
      $[47] = handleDetailReviewId;
      $[48] = t18;
    } else {
      t18 = $[48];
    }
    t17 = photoReviewList.map(t18);
    $[44] = handleDetailReviewId;
    $[45] = photoReviewList;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[49] !== t17) {
    t18 = <div className="flex gap-8">{t17}</div>;
    $[49] = t17;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== emblaRef || $[52] !== t18) {
    t19 = <div className="w-full overflow-hidden" ref={emblaRef}>{t18}</div>;
    $[51] = emblaRef;
    $[52] = t18;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const t20 = !isDesktopViewport && isOpen ? <SessionPortal renderTo={SELECTOR.MAIN_WRAPPER} wrapperClassName="z-10" onClose={() => setIsOpen(false)}><div className="flex flex-col"><ul className="grid grid-cols-3 gap-12 px-20 pt-12">{infinitePhotoReviewList.map(t21 => {
          const {
            fileUrl: file_url,
            reviewId: review_id
          } = t21;
          return <li key={review_id}><button type="button" className={cn("relative aspect-square overflow-hidden rounded-small border border-Gray-black/5 transition-opacity duration-200", "hover:opacity-80", "active:opacity-80", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500")} onClick={() => handleDetailReviewId(review_id)}><Image src={file_url} alt={"\uD3EC\uD1A0\uD6C4\uAE30 \uC774\uBBF8\uC9C0"} width={300} height={300} className="size-full object-cover object-center" /></button></li>;
        })}</ul>{hasNextPage ? <div className="my-30 w-full flex-center" ref={ref}><Spinner /></div> : null}</div></SessionPortal> : <></>;
  const t22 = !isDesktopViewport && !!detailReviewId;
  let t23;
  if ($[54] !== handleDetailReviewId) {
    t23 = () => handleDetailReviewId(undefined);
    $[54] = handleDetailReviewId;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== detailReviewId || $[57] !== t22 || $[58] !== t23) {
    t24 = <ReviewDetailSessionPortal open={t22} onClose={t23} reviewId={detailReviewId} />;
    $[56] = detailReviewId;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t16 || $[61] !== t19 || $[62] !== t20 || $[63] !== t24 || $[64] !== t9) {
    t25 = <section className={t9}>{t16}{t19}{t20}{t24}</section>;
    $[60] = t16;
    $[61] = t19;
    $[62] = t20;
    $[63] = t24;
    $[64] = t9;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  return t25;
};
function _temp(page) {
  return page.context?.contents ?? [];
}